import React, { useState, useCallback, useContext, useMemo, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { keepPreviousData } from '@tanstack/react-query';
import { useAPI } from '../Hooks/useAPIs';
import { TimezoneContext } from '../common/TimezoneContext/TimezoneContext';
import { useDebounce } from '../Hooks/useDebounce';
import FiltersComponent from '../common/FiltersPanel/FIltersPanel';
import FilterDropdown from '../common/FilterDropdown/FilterDropdown';
import {
  Box,
  ThemeProvider,
  Typography,
  Card,
  CardContent,
  Chip,
  Collapse,
  IconButton,
  Paper,
  TablePagination,
  Skeleton,
  Tooltip,
  Grid,
  Divider,
  Badge,
  LinearProgress,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import RouterIcon from '@mui/icons-material/Router';
import CategoryIcon from '@mui/icons-material/Category';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import PublicIcon from '@mui/icons-material/Public';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import UpdateIcon from '@mui/icons-material/Update';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import StorageIcon from '@mui/icons-material/Storage';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import './LoggerLookupView.css';

const VehicleLookupView = ({ activeTheme }) => {
  const [pagination, setPagination] = useState({ page: 1, pageSize: 10 });
  const [expandedVins, setExpandedVins] = useState({});
  const [singleValueFilters, setSingleValueFilters] = useState({
    vin: [],
    mac_id: [],
  });

  const contentRef = useRef(null);
  const { apiCall } = useAPI();
  const { formatDateTime } = useContext(TimezoneContext);

  const debouncedSingleValueFilters = useDebounce(singleValueFilters, 500);

  const handleSingleFilterChange = useCallback((field, value) => {
    setSingleValueFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, []);

  const mapping = { vin: 'vin', mac_id: 'mac_id' };

  const getOtherFilters = useCallback((excludeField) => {
    const other = { ...debouncedSingleValueFilters };
    delete other[excludeField];
    return other;
  }, [debouncedSingleValueFilters]);

  const loggerQuery = useQuery({
    queryKey: ['logger-lookup', pagination.page, pagination.pageSize, debouncedSingleValueFilters],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('page', pagination.page.toString());
      params.append('page_size', pagination.pageSize.toString());
      
      if (debouncedSingleValueFilters.vin?.length > 0) {
        params.append('vin', debouncedSingleValueFilters.vin.join(','));
      }
      if (debouncedSingleValueFilters.mac_id?.length > 0) {
        params.append('mac_id', debouncedSingleValueFilters.mac_id.join(','));
      }

      const response = await apiCall('GET', `api/v1/loggers/lookup/?${params.toString()}`);
      return response.data;
    },
    placeholderData: keepPreviousData,
  });

  const scrollToTop = useCallback(() => {
    contentRef.current?.scrollTo({ top: 0 });
  }, []);

  const handlePageChange = useCallback((event, newPage) => {
    setPagination((prev) => ({ ...prev, page: newPage + 1 }));
    setExpandedVins({});
    scrollToTop();
  }, [scrollToTop]);

  const handleRowsPerPageChange = useCallback((event) => {
    setPagination({ page: 1, pageSize: parseInt(event.target.value, 10) });
    setExpandedVins({});
    scrollToTop();
  }, [scrollToTop]);

  const toggleVinExpansion = useCallback((vin) => {
    setExpandedVins((prev) => ({
      ...prev,
      [vin]: !prev[vin],
    }));
  }, []);

  const vinEntries = useMemo(() => {
    if (!loggerQuery.data?.results) return [];
    return Object.entries(loggerQuery.data.results);
  }, [loggerQuery.data]);

  const totalCount = loggerQuery.data?.count || 0;

  const getCategoryColor = (category) => {
    const colors = {
      logs: 'primary',
      events: 'warning',
      diagnostics: 'secondary',
      metrics: 'success',
    };
    return colors[category.toLowerCase()] || 'default';
  };

  const getFileTypeColor = (fileType) => {
    const colors = {
      csv: 'success',
      json: 'warning',
      xml: 'info',
      log: 'error',
    };
    return colors[fileType.toLowerCase()] || 'default';
  };

  const getRegionLabel = (region) => {
    const labels = {
      NA: 'North America',
      EU: 'Europe',
      APAC: 'Asia Pacific',
      CN: 'China',
    };
    return labels[region?.toUpperCase()] || region;
  };

  const LoadingState = () => (
    <Box className="logger-loading-container">
      {[1, 2, 3].map((i) => (
        <Card key={i} className="logger-vin-card">
          <CardContent>
            <Skeleton variant="text" width="40%" height={32} />
            <Skeleton variant="text" width="60%" height={24} />
            <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
              <Skeleton variant="rounded" width={80} height={24} />
              <Skeleton variant="rounded" width={80} height={24} />
              <Skeleton variant="rounded" width={80} height={24} />
            </Box>
          </CardContent>
        </Card>
      ))}
    </Box>
  );

  const EmptyState = () => (
    <Box className="logger-empty-state">
      <SearchOffIcon className="empty-icon" />
      <Typography variant="h5" className="empty-title">
        No Vehicle Records Found
      </Typography>
      <Typography variant="body1" color="textSecondary">
        There are no vehicle lookup records available at this time.
      </Typography>
    </Box>
  );

  const LoggerRecordCard = ({ record }) => (
    <Paper className="logger-record-card" variant="outlined">
      <Box className="record-header">
        <Box className="record-mac-container">
          <RouterIcon color="action" />
          <Typography variant="subtitle1" fontWeight={500}>
            {record.mac_id}
          </Typography>
        </Box>
        <Chip
          icon={<AnalyticsIcon />}
          label={`${record.total_logs} logs`}
          size="small"
          color="primary"
          variant="outlined"
        />
      </Box>

      <Grid container spacing={2} className="record-details">
        <Grid item xs={12} sm={6} md={3}>
          <Box className="detail-item">
            <CategoryIcon color="action" fontSize="small" />
            <Box>
              <Typography variant="caption" color="textSecondary">
                Categories
              </Typography>
              <Box className="chips-container">
                {record.categories?.map((cat, idx) => (
                  <Chip
                    key={idx}
                    label={cat}
                    size="small"
                    color={getCategoryColor(cat)}
                    variant="outlined"
                  />
                ))}
              </Box>
            </Box>
          </Box>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Box className="detail-item">
            <InsertDriveFileIcon color="action" fontSize="small" />
            <Box>
              <Typography variant="caption" color="textSecondary">
                File Types
              </Typography>
              <Box className="chips-container">
                {record.file_types?.map((type, idx) => (
                  <Chip
                    key={idx}
                    label={type.toUpperCase()}
                    size="small"
                    color={getFileTypeColor(type)}
                    variant="outlined"
                  />
                ))}
              </Box>
            </Box>
          </Box>
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <Box className="detail-item">
            <PublicIcon color="action" fontSize="small" />
            <Box>
              <Typography variant="caption" color="textSecondary">
                Region
              </Typography>
              <Tooltip title={getRegionLabel(record.region_code)}>
                <Chip
                  label={record.region_code}
                  size="small"
                  variant="outlined"
                />
              </Tooltip>
            </Box>
          </Box>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Box className="detail-item">
            <AccessTimeIcon color="action" fontSize="small" />
            <Box className="timestamp-grid">
              <Box className="timestamp-item">
                <Typography variant="caption" color="textSecondary">
                  First Seen
                </Typography>
                <Typography variant="body2">
                  {formatDateTime(record.first_seen)}
                </Typography>
              </Box>
              <Box className="timestamp-item">
                <Typography variant="caption" color="textSecondary">
                  Last Seen
                </Typography>
                <Typography variant="body2" color="primary">
                  {formatDateTime(record.last_seen)}
                </Typography>
              </Box>
              <Box className="timestamp-item">
                <Typography variant="caption" color="textSecondary">
                  OnStar Timestamp
                </Typography>
                <Typography variant="body2">
                  {formatDateTime(record.onstar_last_seen)}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Grid>
      </Grid>

      <Divider sx={{ my: 1.5 }} />

      <Box className="record-footer">
        <Typography variant="caption" color="textSecondary">
          <UpdateIcon fontSize="inherit" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
          Updated: {formatDateTime(record.updated_at)}
        </Typography>
        <Typography variant="caption" color="textSecondary">
          ID: {record.id}
        </Typography>
      </Box>
    </Paper>
  );

  const VinCard = ({ vin, records }) => {
    const isExpanded = expandedVins[vin];
    const totalLogs = records.reduce((sum, r) => sum + (r.total_logs || 0), 0);
    const latestSeen = records.reduce((latest, r) => {
      const date = new Date(r.last_seen);
      return date > latest ? date : latest;
    }, new Date(0));

    const uniqueCategories = [...new Set(records.flatMap((r) => r.categories || []))];
    const uniqueRegions = [...new Set(records.map((r) => r.region_code).filter(Boolean))];

    return (
      <Card className={`logger-vin-card ${isExpanded ? 'expanded' : ''}`} variant="outlined">
        <Box className="vin-card-header" onClick={() => toggleVinExpansion(vin)}>
          <Box className="vin-info">
            <Box className="vin-title-row">
              <DirectionsCarIcon color="primary" />
              <Typography variant="h6" className="vin-number">
                {vin}
              </Typography>
              <Badge badgeContent={records.length} color="primary">
                <StorageIcon fontSize="small" color="action" />
              </Badge>
            </Box>
            <Box className="vin-meta">
              <Chip
                icon={<AnalyticsIcon />}
                label={`${totalLogs} total logs`}
                size="small"
                variant="outlined"
              />
              {uniqueCategories.slice(0, 3).map((cat, idx) => (
                <Chip
                  key={idx}
                  label={cat}
                  size="small"
                  color={getCategoryColor(cat)}
                  variant="outlined"
                />
              ))}
              {uniqueCategories.length > 3 && (
                <Chip label={`+${uniqueCategories.length - 3}`} size="small" variant="outlined" />
              )}
              {uniqueRegions.map((region, idx) => (
                <Chip key={idx} label={region} size="small" variant="outlined" />
              ))}
            </Box>
          </Box>
          <Box className="vin-actions">
            <Typography variant="caption" color="textSecondary">
              Last activity: {formatDateTime(latestSeen)}
            </Typography>
            <IconButton
              className={`expand-button ${isExpanded ? 'expanded' : ''}`}
              size="small"
            >
              <ExpandMoreIcon />
            </IconButton>
          </Box>
        </Box>

        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <CardContent className="vin-card-content">
            <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <RouterIcon fontSize="small" />
              {records.length} Associated Device{records.length !== 1 ? 's' : ''}
            </Typography>
            <Box className="records-container">
              {records.map((record) => (
                <LoggerRecordCard key={record.id} record={record} />
              ))}
            </Box>
          </CardContent>
        </Collapse>
      </Card>
    );
  };

  return (
    <ThemeProvider theme={activeTheme}>
      <Box className="vehicle-lookup-main-container">
        <Box>
          <FiltersComponent
            activeTheme={activeTheme}
            dropdowns={
              <Box className="logger-lookup-filters" sx={{ mt: 2 }}>
                <FilterDropdown
                  field="vin"
                  label="VIN"
                  value={singleValueFilters.vin}
                  onChange={(value) => handleSingleFilterChange('vin', value)}
                  apiBaseUrl="/api/v1/loggers/lookup"
                  multiple={true}
                  disablePopperFlip={true}
                  icon={DirectionsCarIcon}
                  otherFilters={getOtherFilters('vin')}
                  fieldMapping={mapping}
                />
                <FilterDropdown
                  field="mac_id"
                  label="MAC ID"
                  value={singleValueFilters.mac_id}
                  onChange={(value) => handleSingleFilterChange('mac_id', value)}
                  apiBaseUrl="/api/v1/loggers/lookup"
                  multiple={true}
                  disablePopperFlip={true}
                  icon={RouterIcon}
                  otherFilters={getOtherFilters('mac_id')}
                  fieldMapping={mapping}
                />
              </Box>
            }
          />
        </Box>
        <Box className="vehicle-lookup-content-wrapper">
          <Paper className="vehicle-lookup-header" elevation={0} square>
            <Box className="header-content">
              <DirectionsCarIcon color="primary" sx={{ fontSize: 28 }} />
              <Typography variant="h6" fontWeight={600}>
                Vehicle Lookup
              </Typography>
            </Box>
            <Box className="header-stats">
              <Box className="stat-item">
                <Typography variant="subtitle1" color="primary" fontWeight={600}>
                  {totalCount}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Total VINs
                </Typography>
              </Box>
              <Divider orientation="vertical" flexItem />
              <Box className="stat-item">
                <Typography variant="subtitle1" color="primary" fontWeight={600}>
                  {vinEntries.reduce((sum, [, records]) => sum + records.length, 0)}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Devices
                </Typography>
              </Box>
            </Box>
            {loggerQuery.isFetching && <LinearProgress className="loading-bar" />}
          </Paper>

          <Box className="vehicle-lookup-content" ref={contentRef}>
            {loggerQuery.isLoading ? (
              <LoadingState />
            ) : vinEntries.length === 0 ? (
              <EmptyState />
            ) : (
              <Box className="vin-cards-container">
                {vinEntries.map(([vin, records]) => (
                  <VinCard key={vin} vin={vin} records={records} />
                ))}
              </Box>
            )}
          </Box>

          <Paper className="pagination-container" elevation={0} square>
            <TablePagination
              component="div"
              count={totalCount}
              page={pagination.page - 1}
              onPageChange={handlePageChange}
              rowsPerPage={pagination.pageSize}
              onRowsPerPageChange={handleRowsPerPageChange}
              rowsPerPageOptions={[5, 10, 25, 50]}
              labelRowsPerPage="VINs per page:"
              showFirstButton
              showLastButton
            />
          </Paper>
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default VehicleLookupView;
